/**
 * @file: conversationTitle.ts
 *
 * Compose the conversation's display title from the room handle, the live
 * member count, and who's currently typing. Pure — the header components
 * (in-pane on desktop, the navigator title on mobile) both render from this,
 * so the "fold typing under the room name" rule lives in ONE place.
 */

import {
  ChatConversationHandle,
  isGroupRoom,
  roomTitle,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { clampGroupTitleNames } from './chatThreadDisplay';

export interface ConversationTitle {
  title: string;
  /** Present only while someone is typing ("张三 正在输入..."). */
  subtitle?: string;
}

export function composeConversationTitle(
  conversationHandle: ChatConversationHandle,
  memberCount: number,
  typingNames: string[],
): ConversationTitle {
  const base = roomTitle(conversationHandle) || '聊天';
  // Large groups carry a long `A、B、C…` auto title; clamp the names so the
  // `(N)` count stays visible and the header doesn't run under the ⋮ button.
  const title =
    isGroupRoom(conversationHandle) && memberCount > 0
      ? `${clampGroupTitleNames(base)} (${memberCount})`
      : base;
  // In a private chat the header already shows the peer's name, so naming the
  // typist again is redundant — just "正在输入...". Groups prefix the name(s).
  const subtitle =
    typingNames.length > 0
      ? isGroupRoom(conversationHandle)
        ? `${typingNames.join('、')} 正在输入...`
        : '正在输入...'
      : undefined;
  return { title, subtitle };
}
